import type { CreateLinkTokenBody } from '@/api/custom';
import {
  // getVerificationStatus,
  // retryIdentity,
  createLinkToken,
  getIdentityApiBaseUrl,
} from '@/api/custom';
import { getPlaidRedirectUrl } from '@/config/domains';
import { User } from '@/store/features/userinfo/userinfoSlice';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeDateOfBirth(dateOfBirth: string | null | undefined) {
  const trimmed = dateOfBirth?.trim();
  if (!trimmed) return undefined;

  const datePart = trimmed.split('T')[0];
  if (!ISO_DATE_PATTERN.test(datePart)) return undefined;

  const parsed = new Date(`${datePart}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return parsed.toISOString().slice(0, 10) === datePart ? datePart : undefined;
}

function buildCreateLinkTokenBody(
  user: User,
  override?: Partial<CreateLinkTokenBody>,
): CreateLinkTokenBody | undefined {
  const nameParts = user?.name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const firstName = override?.first_name?.trim() || nameParts[0];
  const lastName = override?.last_name?.trim() || nameParts.slice(1).join(' ');
  const dateOfBirth = normalizeDateOfBirth(override?.date_of_birth ?? user?.date_of_birth);

  if (!firstName || !lastName || !dateOfBirth) {
    return undefined;
  }

  const email = override?.email?.trim() || user?.email?.trim();

  return {
    first_name: firstName,
    last_name: lastName,
    ...(email ? { email } : {}),
    date_of_birth: dateOfBirth,
    ...(override?.phone_number?.trim() ? { phone_number: override.phone_number.trim() } : {}),
    ...(override?.street?.trim() ? { street: override.street.trim() } : {}),
    ...(override?.city?.trim() ? { city: override.city.trim() } : {}),
    ...(override?.state?.trim() ? { state: override.state.trim() } : {}),
    ...(override?.zip_code?.trim() ? { zip_code: override.zip_code.trim() } : {}),
  };
}

export async function loginPlaid(
  user: User,
  override?: Partial<CreateLinkTokenBody>,
): Promise<boolean> {
  try {
    // const { status } = await getVerificationStatus();
    // if (status === "failed") {
    //   await retryIdentity();
    // }

    const userInfo = buildCreateLinkTokenBody(user, override);
    if (!userInfo) {
      throw new Error('Personal information is required before starting KYC.');
    }

    const { link_token } = await createLinkToken(userInfo);

    // open Plaid Link in the web environment
    return await openPlaidLink(link_token);
  } catch (error) {
    console.error('Login Plaid error:', error);
    return false;
  }
}

declare global {
  interface Window {
    Plaid?: unknown;
  }
}

export async function openPlaidLink(link_token: string): Promise<boolean> {
  const baseUrl = await getIdentityApiBaseUrl();

  // generate the redirectUrl based on the environment
  const redirectUrl = getPlaidRedirectUrl();
  const url = `${baseUrl}?link_token=${encodeURIComponent(link_token)}&redirect_uri=${encodeURIComponent(redirectUrl)}`;

  // redirect to Plaid
  window.location.assign(url);

  // since this is a page redirect, the Promise will not resolve
  // but we return true to indicate the redirect has been started
  return true;
}
